import {TextObservable} from "../../../observables/textObservable";
import {DateObservable} from "../../../observables/dateObservable";
import {EventStartAndEndDetails} from "./eventStartAndEndDetails";
import {EventBookingDeadlineDetails} from "./eventBookingDeadlineDetails";
import {EventPriceDetails} from "./eventPriceDetails";
import {computed, makeObservable, observable, runInAction} from "mobx";
import {hasSpaces, isWebLink} from "../../../../utils/stringUtils";
import {ImageObservable} from "../../../observables/imageObservable";
import {BooleanObservable} from "../../../observables/booleanObservable";
import {EventPaidType} from "../../../../shared/enums/eventPaidType";
import {KeyValue} from "../../../observables/keyValue";
import {
  descriptionMaxLength,
  eventDescriptionMaxLength,
  titleMaxLength,
  maxWeeksBeforeEvent,
  maxDaysBeforeEvent,
  oneDayInMilliseconds,
  recurrenceDaily
} from "../../../../shared/constants";
import {personTypeIds} from "../../../../shared/personTypeIds";
import {ObservableList} from "../../../observables/observableList";
import {EventBookingSpaceDetails} from "./eventBookingSpaceDetails";
import {addDays, addHours, addMinutes, sub} from "date-fns";
import {MultiVillageSelectObservableList} from "../../../observables/multiVillageSelectObservableList";
import {EventStatus} from "../../../../shared/enums/eventStatus";
import {utcToZonedTime} from "date-fns-tz";
import {EventRecurrenceDetails} from "./eventRecurrenceDetails";
import {RecurringStatus} from "../../../../shared/enums/RecurringStatus";

export const CustomLocationConstant = "custom";
export const VirtualEventConstant = "virtualEvent";
const locationMaxLength = 75;
const virtualEventMaxLength = 200;

export default class EventDetails {
  timeZone: string;
  id: string;
  title: TextObservable;
  category: ObservableList<KeyValue>;
  location: ObservableList<KeyValue>;
  villages: MultiVillageSelectObservableList;
  customLocation: TextObservable;
  virtualLocation: TextObservable;
  locationDisplayName: string; //Using for displaying in modal and for editing custom locations
  categoryName?: string;
  description: TextObservable;
  reason: TextObservable;
  readonly eventDateDetails: EventStartAndEndDetails;
  bookingDetails: EventBookingDeadlineDetails;
  bookingDeadlineDate: Date | null;
  readonly isPassedBookingDeadline: boolean;
  bookingSpacesDetails: EventBookingSpaceDetails;
  priceDetails: EventPriceDetails;
  image: ImageObservable;
  isFeatured: BooleanObservable;
  selectedVillageId?: string | null;
  villageIds?: string[] | null;
  templateId?: string;
  templateDurationHour?: number;
  templateDurationMin?: number;
  hasPaidAttendees?: boolean;
  isIndependentEventReleaseFeatureToggleEnabled?: boolean;
  isMultiVillage: boolean;
  readonly isEditMode?: boolean;
  isReleased?: boolean;
  readonly isPassedEndDate: boolean;
  isBeforeStartDate: boolean;
  eventStatus: EventStatus;
  readonly eventRecurrenceDetails: EventRecurrenceDetails;
  recurringStatus: RecurringStatus;
  baseEventId?: string;
  isEditingEventSeriesOccurrence?: boolean;
  releaseDate: DateObservable;
  originalStartDate?: Date;
  personTypes?: string[] = [];

  constructor(
    timeZone: string,
    id?: string,
    title?: string,
    category?: string,
    locationId?: string,
    locationName?: string,
    description?: string,
    eventDateStart?: Date,
    eventDateEnd?: Date,
    isAllDay?: boolean,
    price?: {
      amount: number | null;
      costAllocation?: string;
    },
    bookingDeadline?: string,
    bookingCapacity?: number,
    imageFileId?: string,
    isFeatured?: boolean,
    locationText?: string,
    templateId?: string,
    templateDurationHour?: number,
    templateDurationMin?: number,
    totalSpace?: number | undefined,
    reservedSpace?: number | undefined,
    totalBookings?: number | undefined,
    totalStaffAssistedBookings?: number | undefined,
    capacityRemaining?: number | undefined,
    reservedCapacityRemaining?: number | undefined,
    hasPaidAttendees?: boolean,
    totalPaidBookings?: number,
    isMultiVillage?: boolean,
    villageIds?: string[] | undefined,
    categoryName?: string | undefined,
    isReleased?: boolean | undefined,
    reason?: string,
    eventStatus?: EventStatus,
    eventRecurrenceDetails?: EventRecurrenceDetails,
    recurringStatus?: RecurringStatus,
    baseEventId?: string,
    highestBookingCountForSeries?: number,
    isEditingEventSeriesOccurrence?: boolean,
    totalWaitlistedBookings?: number | undefined,
    releaseDate?: Date | undefined,
    personTypes?: string[]
  ) {
    makeObservable(this, {
      canPublish: computed,
      hasChanges: computed,
      hasChangesIgnoringRecurrenceEndDate: computed,
      bookingDetails: observable,
      isPassedEndDate: observable,
      isBeforeStartDate: observable
    });
    this.timeZone = timeZone;
    this.id = id ?? "";
    this.isEditMode = !!id;
    this.hasPaidAttendees = hasPaidAttendees;
    this.isMultiVillage = isMultiVillage ?? false;
    this.eventStatus = eventStatus ?? EventStatus.Published; // event status is never undefined if coming from CMS
    this.recurringStatus = recurringStatus ?? RecurringStatus.OneOff;
    this.villageIds = villageIds as string[];
    this.title = new TextObservable(title ?? "", titleMaxLength, (value) => {
      return value?.trim() !== "";
    });
    this.category = new ObservableList<KeyValue>([], category, (value) => {
      return value !== null && value !== undefined;
    });
    this.categoryName = categoryName;
    this.isReleased = isReleased;
    this.isEditingEventSeriesOccurrence = isEditingEventSeriesOccurrence;
    this.originalStartDate = eventDateStart;
    this.location = new ObservableList<KeyValue>(
      [],
      locationId,
      (value) => !!value,
      () => {
        //Defaulted to custom or virtual event so use passed in name
        if (isWebLink(this.locationDisplayName)) {
          return VirtualEventConstant;
        } else {
          return CustomLocationConstant;
        }
      }
    );
    this.customLocation = new TextObservable(
      "",
      locationMaxLength,
      (value) =>
        this.location.selectedItem?.value !== CustomLocationConstant ||
        (value !== "" && value !== null && value !== undefined)
    );
    this.virtualLocation = new TextObservable(
      "",
      virtualEventMaxLength,
      (value) =>
        this.location.selectedItem?.value !== VirtualEventConstant ||
        (value !== "" &&
          value !== null &&
          value !== undefined &&
          isWebLink(value) &&
          !hasSpaces(value))
    );
    this.locationDisplayName = locationName ?? "";

    isWebLink(this.locationDisplayName)
      ? this.virtualLocation.setText(this.locationDisplayName)
      : this.customLocation.setText(this.locationDisplayName);

    this.description = new TextObservable(
      description ?? "",
      eventDescriptionMaxLength,
      (value) => {
        return value !== "" && value !== null && value !== undefined;
      }
    );

    this.reason = new TextObservable(
      reason ?? "",
      descriptionMaxLength,
      (value) => {
        return value !== "" && value !== null && value !== undefined;
      }
    );

    const timeOneHourInFuture = utcToZonedTime(
      addHours(new Date(), 1),
      this.timeZone
    );

    const startDate = new DateObservable(
      eventDateStart ?? null,
      timeOneHourInFuture,
      (toValidate) => {
        const dateValidation =
          !!toValidate &&
          (utcToZonedTime(toValidate, this.timeZone).getTime() >=
            utcToZonedTime(new Date(), this.timeZone).getTime() ||
            //RECs can change start/end time of recurring events, even if series start date is in the past.
            //This check prevents validation error when editing recurring event time.
            (!!this.isEditMode && this.isRecurringEvent));

        if (!this.isEditMode)
          return (
            dateValidation &&
            utcToZonedTime(toValidate, this.timeZone).getTime() >=
              timeOneHourInFuture.getTime()
          );
        else return dateValidation;
      }
    );
    const endDate = new DateObservable(
      eventDateEnd ?? null,
      addMinutes(timeOneHourInFuture, 15),
      (toValidate) => {
        return (
          !!startDate.date &&
          !!toValidate &&
          toValidate.getTime() > startDate.date.getTime() &&
          (toValidate.getTime() >=
            utcToZonedTime(new Date(), this.timeZone).getTime() ||
            //RECs can change start/end time of recurring events, even if series start date is in the past.
            //This check prevents validation error when editing recurring event time.
            (!!this.isEditMode && this.isRecurringEvent))
        );
      }
    );

    const startDateTime = startDate?.date?.getTime();
    const endDateTime = endDate?.date?.getTime();
    const now = new Date().getTime();

    const eventEndDate =
      isEditingEventSeriesOccurrence ||
      this.recurringStatus === RecurringStatus.DetachedOccurrence
        ? endDateTime
        : eventRecurrenceDetails?.recurrenceEndDate.date?.getTime() ??
          endDateTime;
    this.isPassedEndDate = (eventEndDate ?? 0) < now && this.isEditMode;
    this.isBeforeStartDate = now < (startDateTime ?? 0);

    this.villages = new MultiVillageSelectObservableList([], []);

    this.eventDateDetails = new EventStartAndEndDetails(
      this.timeZone,
      startDate,
      endDate,
      !isReleased,
      templateDurationHour,
      templateDurationMin
    );

    this.bookingDetails = new EventBookingDeadlineDetails(
      bookingDeadline,
      bookingCapacity ?? 0,
      true
    );

    this.bookingDeadlineDate = this.getPassedBookingDeadline(
      eventDateStart,
      this.bookingDetails
    );

    this.isPassedBookingDeadline =
      this.bookingDeadlineDate !== null
        ? this.bookingDeadlineDate.getTime() < now
        : false;

    this.setMaxDeadlineSelectValues(
      this.eventDateDetails.startDate.date ?? null
    );

    if (this.isEditMode && !bookingDeadline) {
      this.reinitializeBookingDeadlineOnPrice(EventPaidType.free, false, false);
      this.bookingDetails.hours.setInitialValue();
    }
    this.bookingSpacesDetails = new EventBookingSpaceDetails(
      totalSpace,
      reservedSpace,
      totalBookings,
      totalStaffAssistedBookings,
      capacityRemaining,
      reservedCapacityRemaining,
      totalPaidBookings,
      highestBookingCountForSeries,
      totalWaitlistedBookings
    );
    if (totalSpace)
      this.bookingSpacesDetails.reservedSpace.setIsDisabled(false);

    this.priceDetails = new EventPriceDetails(price, true);
    this.image = new ImageObservable(
      imageFileId ?? undefined,
      (toValidate) =>
        this.personTypes?.every((p) => p !== personTypeIds.independent) ||
        toValidate !== null
    );
    this.isFeatured = new BooleanObservable(isFeatured ?? false);

    this.templateId = templateId;
    this.templateDurationHour = templateDurationHour;
    this.templateDurationMin = templateDurationMin;

    this.eventRecurrenceDetails =
      eventRecurrenceDetails ?? new EventRecurrenceDetails(timeZone);

    this.eventRecurrenceDetails.recurrenceEndDate.validate = (date) =>
      !date ||
      !this.eventDateDetails.endDate.date ||
      date.getTime() >= this.eventDateDetails.endDate.date.getTime();

    this.baseEventId = baseEventId;
    this.releaseDate = new DateObservable(
      releaseDate,
      addHours(utcToZonedTime(new Date(), this.timeZone), 1),
      (toValidate) => {
        if (!toValidate) {
          return true;
        }
        return (
          addDays(this.deadlineClosedDate, -3).getTime() >= toValidate.getTime()
        );
      }
    );
    this.personTypes = personTypes;
  }

  reinitializeBookingDeadlineOnPrice = (
    value: string,
    isReleased: boolean,
    isEditing: boolean
  ) => {
    const isNotFixed =
      EventPaidType.free === value || EventPaidType.ownExpense === value;
    this.setMaxDeadlineSelectValues(this.eventDateDetails.startDate.date);
    if (!isNotFixed) this.bookingDetails.weeks.setSelectedItem("0");
    this.bookingDetails.days.setSelectedItem(isNotFixed ? "0" : "5");
    this.bookingDetails.hours.setSelectedItem(isNotFixed ? "1" : "0");

    if (
      !this.bookingDetails.hasBookingDeadline &&
      value === EventPaidType.fixed &&
      (!isReleased || !isEditing)
    )
      this.bookingDetails.setHasBookingDeadline(true);
  };

  getPassedBookingDeadline = (
    eventStartDate: Date | undefined,
    bookingDeadlineDetails: EventBookingDeadlineDetails
  ) => {
    if (eventStartDate && bookingDeadlineDetails.hasBookingDeadline) {
      const bookingDeadline = sub(eventStartDate, {
        weeks: Number(bookingDeadlineDetails.weeks.selectedItem?.value) ?? 0,
        days: Number(bookingDeadlineDetails.days.selectedItem?.value) ?? 0,
        hours: Number(bookingDeadlineDetails.hours.selectedItem?.value) ?? 0
      });

      return bookingDeadline;
    }
    return null;
  };

  setMaxDeadlineSelectValues = (eventStartDate: Date | null) => {
    if (
      !eventStartDate ||
      !this.isIndependentEventReleaseFeatureToggleEnabled
    ) {
      return;
    }

    let daysBeforeEvent = Math.floor(
      Math.max(
        0,
        eventStartDate.getTime() -
          utcToZonedTime(new Date(), this.timeZone).getTime()
      ) / oneDayInMilliseconds
    );
    const weeksBeforeEvent = Math.floor(daysBeforeEvent / 7);

    let currentWeeksSelected = Number(
      this.bookingDetails.weeks.selectedItem?.value ?? 0
    );

    if (weeksBeforeEvent < currentWeeksSelected) {
      this.bookingDetails.weeks.setSelectedItem(weeksBeforeEvent.toString());
      currentWeeksSelected = weeksBeforeEvent;
    }

    this.bookingDetails.weeks.setItems(
      this.bookingDetails.generateSelectItems(
        Math.min(maxWeeksBeforeEvent, weeksBeforeEvent),
        "week"
      )
    );

    daysBeforeEvent -= currentWeeksSelected * 7;

    // Event with fixed price is set to deadline of five days,
    // Override daysBeforeEvent in this case
    if (this.priceDetails?.paidType.text === EventPaidType.fixed) {
      daysBeforeEvent = Math.max(5, daysBeforeEvent);
    }

    const currentDaysSelected = Number(
      this.bookingDetails.days.selectedItem?.value ?? 0
    );

    if (daysBeforeEvent < currentDaysSelected) {
      this.bookingDetails.days.setSelectedItem(daysBeforeEvent.toString());
    }

    this.bookingDetails.days.setItems(
      this.bookingDetails.generateSelectItems(
        Math.min(maxDaysBeforeEvent, daysBeforeEvent),
        "day"
      )
    );
  };

  setIsDirty = (value: boolean) => {
    runInAction(() => {
      this.category.setIsDirty(value);
      this.title.setIsDirty(value);
      this.location.setIsDirty(value);
      this.customLocation.setIsDirty(value);
      this.virtualLocation.setIsDirty(value);
      this.description.setIsDirty(value);
      this.eventDateDetails.setIsDirty(value);
      this.bookingDetails.setIsDirty(value);
      this.bookingDetails.setDatesAreDirty(value);
      this.priceDetails.setIsDirty(value);
      this.priceDetails.price.setIsDirty(value);
      this.priceDetails.paidType.setIsDirty(value);
      this.priceDetails.costAllocation.setIsDirty(value);
      this.image.setIsDirty(value);
      this.isFeatured.setIsDirty(value);
      this.bookingSpacesDetails.totalSpace.setIsDirty(value);
      this.bookingSpacesDetails.reservedSpace.setIsDirty(value);
      this.villages.setIsDirty(value);
      this.eventRecurrenceDetails.recurrenceEndDate.setIsDirty(value);
    });
  };

  get canPublish() {
    let isPriceValid = this.bookingDetails.hasBookingDeadline
      ? this.priceDetails.isValid
      : true;
    let isSpacesDetailsValid = this.bookingDetails.hasBookingDeadline
      ? this.bookingSpacesDetails.isValid
      : true;
    let isStartDateValid = true;
    let isMultiVillageSelectionValid = true;

    if (!this.isEditMode)
      isStartDateValid = this.eventDateDetails.isStartDateOneHourInFuture;

    if (this.isMultiVillage) {
      isMultiVillageSelectionValid = this.villages.isValid;
    }
    return (
      this.hasChanges &&
      this.title.isValid &&
      this.category.isValid &&
      this.location.isValid &&
      this.customLocation.isValid &&
      this.virtualLocation.isValid &&
      this.description.isValid &&
      this.eventDateDetails.isValid &&
      isStartDateValid &&
      this.bookingDetails.isValid &&
      this.image.isValid &&
      isPriceValid &&
      isSpacesDetailsValid &&
      isMultiVillageSelectionValid &&
      this.eventRecurrenceDetails.isValid &&
      this.releaseDate.isValid
    );
  }

  get canUpdateCancelledPostponedEvent() {
    return this.hasChanges && this.reason.isValid;
  }

  get hasChanges() {
    return (
      this.hasChangesIgnoringRecurrenceEndDate ||
      this.eventRecurrenceDetails.recurrenceEndDate.isDirty
    );
  }

  get hasChangesIgnoringRecurrenceEndDate() {
    return (
      this.title.isDirty ||
      this.category.isDirty ||
      this.location.isDirty ||
      this.customLocation.isDirty ||
      this.virtualLocation.isDirty ||
      this.description.isDirty ||
      this.reason.isDirty ||
      !this.eventDateDetails.checkIsDirty() ||
      this.bookingDetails.datesAreDirty ||
      this.bookingDetails.isDirty ||
      this.bookingSpacesDetails.checkIsDirty() ||
      this.priceDetails.isDirty ||
      this.image.isDirty ||
      this.isFeatured.isDirty ||
      this.villages.isDirty ||
      this.releaseDate.isDirty ||
      this.eventRecurrenceDetails.monthWeeks.hasChange()
    );
  }

  get requestObject() {
    // using W3C duration spec https://www.w3.org/TR/xmlschema-2/#duration
    let weeks = Number(
      resolveBookingDetailsSelectedItem(this.bookingDetails.weeks.selectedItem)
    );
    let days = Number(
      resolveBookingDetailsSelectedItem(this.bookingDetails.days.selectedItem)
    );
    const daysDeadline = days + weeks > 0 ? `${weeks * 7 + days}D` : "";
    const hoursDeadline =
      resolveBookingDetailsSelectedItem(
        this.bookingDetails.hours.selectedItem
      ) !== "0"
        ? `T${this.bookingDetails.hours.selectedItem?.value}H`
        : "";

    function resolveBookingDetailsSelectedItem(selectedItem?: KeyValue | null) {
      return selectedItem == null ? "0" : selectedItem.value;
    }

    function resolveLocation(
      isCustomLocationTextChanged: boolean,
      isVirtualLocationTextChanged: boolean,
      locationId: string | undefined,
      location: string | undefined,
      customLocation: string,
      virtualLocation: string
    ) {
      switch (location) {
        case CustomLocationConstant:
          return isCustomLocationTextChanged
            ? {name: customLocation}
            : {id: locationId};
        case VirtualEventConstant:
          return isVirtualLocationTextChanged
            ? {name: virtualLocation}
            : {id: locationId};
        default:
          return {id: location};
      }
    }

    let capacity = this.bookingSpacesDetails.totalSpace.number || null;

    let price =
      this.priceDetails.isPaidEvent &&
      this.priceDetails.paidType.text === EventPaidType.ownExpense
        ? null
        : {
            amount: this.priceDetails.isPaidEvent
              ? this.priceDetails.price.number
              : 0,
            costAllocation: this.priceDetails.costAllocation.selectedItem?.value
          };

    if (!this.isMultiVillage) {
      this.villageIds = [this.selectedVillageId!];
    } else if (this.villages.allVillageOptionSelected()) {
      // Event Api expects null villageIds list to be sent when creating an event for all villages.
      this.villageIds = null;
    } else {
      this.villageIds = this.villages.selectedItems.map((value) => value.value);
    }

    let recurrence = null;

    if (
      this.eventRecurrenceDetails.isRecurringEvent &&
      !this.isEditingEventSeriesOccurrence &&
      this.recurringStatus !== RecurringStatus.DetachedOccurrence
    ) {
      recurrence = {
        frequency: this.eventRecurrenceDetails.frequency,
        interval: this.eventRecurrenceDetails.interval,
        endDate: this.eventRecurrenceDetails.getRecurrenceEndDate(
          this.eventDateDetails.endDate.date!
        ),
        monthRecurrence: this.eventRecurrenceDetails.monthRecurrence(
          this.isEditMode ?? false
        ),
        monthWeeks:
          this.eventRecurrenceDetails.monthWeeks.getSelectedWeekNumbers(),
        weekdays:
          this.eventRecurrenceDetails.frequency === recurrenceDaily
            ? this.eventRecurrenceDetails.weekdays.getSelectedDaysByInitial()
            : null
      };
    }
    const hasCustomLocationChanged =
      this.customLocation?.text !== this.locationDisplayName;
    const hasVirtualLocationChanged =
      this.virtualLocation?.text !== this.locationDisplayName;
    return {
      id: {
        id: this.id
      },
      title: this.title.text.trim(),
      featured: this.isFeatured.value,
      description: this.description.text,
      startDate: this.eventDateDetails.startDate.date?.toISOString(),
      endDate: this.eventDateDetails.endDate.date?.toISOString(),
      location: resolveLocation(
        hasCustomLocationChanged,
        hasVirtualLocationChanged,
        this.location.initialValue ?? undefined,
        this.location.selectedItem?.value,
        this.customLocation.text.trim(),
        this.virtualLocation.text.trim()
      ),
      category: {
        id: this.category.selectedItem?.value,
        name: this.category.selectedItem?.label
      },
      tags: null,
      price: price,
      isAllDay: false,
      capacity: this.bookingDetails.hasBookingDeadline ? capacity : null,
      reservedCapacity: this.bookingDetails.hasBookingDeadline
        ? this.bookingSpacesDetails.reservedSpace.number || null
        : null,
      bookingDeadline: this.bookingDetails.hasBookingDeadline
        ? `P${daysDeadline}${hoursDeadline}`
        : null,
      status: this.eventStatus,
      statusChangeReason: this.reason.text,
      recurrence: recurrence,
      primaryImage: this.image.fileId
        ? {
            fileId: this.image.fileId
          }
        : null,
      villageIds: this.villageIds,
      templateId: this.templateId,
      recurringStatus: this.recurringStatus,
      releaseDate: this.releaseDate.date?.toISOString(),
      personTypes: this.personTypes
    };
  }

  get isDeadLinePassed() {
    if (
      this.eventDateDetails.startDate &&
      this.bookingDetails.initialBookingDeadline
    ) {
      const weekValue = Number(this.bookingDetails.weeks.initialValue || 0);
      const dayValue = Number(this.bookingDetails.days.initialValue || 0);
      const hoursValue = Number(this.bookingDetails.hours.initialValue || 0);
      const timezonedStartDate = utcToZonedTime(
        new Date(this.eventDateDetails.startDate.initialValue),
        this.timeZone
      );
      const date = addHours(
        addDays(timezonedStartDate, -(weekValue * 7 + dayValue)),
        -hoursValue
      );

      return (
        date.getTime() <= utcToZonedTime(new Date(), this.timeZone).getTime()
      );
    }
    return false;
  }

  get deadlineClosedDate() {
    let eventStartDate =
      this.eventDateDetails.startDate.date ??
      this.eventDateDetails.startDate.initialValue;

    const weekValue = Number(
      this.bookingDetails.weeks.selectedItem?.value || 0
    );
    const dayValue = Number(this.bookingDetails.days.selectedItem?.value || 0);
    const hoursValue = Number(
      this.bookingDetails.hours.selectedItem?.value || 0
    );
    const timezonedStartDate = utcToZonedTime(eventStartDate, this.timeZone);
    return addHours(
      addDays(timezonedStartDate, -(weekValue * 7 + dayValue)),
      -hoursValue
    );
  }

  get isBookingReopen() {
    if (
      this.eventDateDetails.startDate.date &&
      this.bookingDetails.initialBookingDeadline
    ) {
      const dayValue = Number(
        this.bookingDetails.days.selectedItem?.value || 0
      );
      const hoursValue = Number(
        this.bookingDetails.hours.selectedItem?.value || 0
      );
      const date = addHours(
        addDays(this.eventDateDetails.startDate.date, -dayValue),
        -hoursValue
      );
      return (
        utcToZonedTime(new Date(), this.timeZone).getTime() <= date.getTime()
      );
    }
    return false;
  }

  get isRecurringEvent() {
    return (
      this.recurringStatus === RecurringStatus.Series ||
      this.recurringStatus === RecurringStatus.DetachedSeries
    );
  }

  get hasNonCosmeticChanges() {
    return (
      this.bookingDetails.initialBookingDeadline !==
        this.bookingDetails.hasBookingDeadline ||
      (!this.bookingSpacesDetails.isUnlimitedCapacity &&
        this.bookingSpacesDetails
          .totalSpaceBelowHighestBookingCountForSeries) ||
      this.eventDateDetails.isEventTimeChanged() ||
      this.eventRecurrenceDetails.monthWeeks.hasChange()
    );
  }

  get getOccurrenceDate() {
    if (this.isRecurringEvent) {
      return this.eventDateDetails.startDate.date;
    }
    return null;
  }

  getEventSeriesId = (): string | undefined => {
    return this.recurringStatus === RecurringStatus.DetachedOccurrence
      ? this.baseEventId
      : this.id;
  };

  setReleaseDateFromBookingDeadline = (ignoreAlreadyNull: boolean) => {
    // Only set release date if it already has a value and not in past, or if ignore is specified
    const currentTime = utcToZonedTime(new Date(), this.timeZone).getTime();

    if (
      ignoreAlreadyNull ||
      (this.releaseDate.date && this.releaseDate.date.getTime() > currentTime)
    ) {
      const deadlineClosedDate = this.deadlineClosedDate;

      if (addDays(deadlineClosedDate!, -7).getTime() > currentTime) {
        this.releaseDate.setDate(addDays(deadlineClosedDate!, -7));
      } else {
        this.releaseDate.setDate(addDays(currentTime, 1));
      }
    }
  };
}
